import { useBooks } from '../hooks/useBooks'
import type { Book, BookRole } from '../types'
import './TopologyDiagram.css'

const ROLE_LABEL: Record<BookRole, string> = {
  research: 'Research',
  validation: 'Validation',
  live: 'Live',
  book: 'Book',
}

// Fixed layout slots — research on the left as the source, validation and
// live fed from it on the right, stacked.
const NODE_POS: Record<'research' | 'validation' | 'live', { x: number; y: number }> = {
  research: { x: 70, y: 110 },
  validation: { x: 330, y: 50 },
  live: { x: 330, y: 170 },
}

const NODE_W = 130
const NODE_H = 56

function nodeClass(book: Book | undefined): string {
  if (!book) return 'topo-node topo-node--absent'
  if (book.bookStatus === null) return 'topo-node topo-node--dormant'
  if (book.bookStatus.halted) return 'topo-node topo-node--halted'
  return 'topo-node topo-node--running'
}

/**
 * Small crisp SVG of the three-book feed topology: research is the
 * picking machine; validation and live mirror its HELD set via
 * pick_source_account_id. Not a chart — hand-laid-out nodes + arrows.
 */
export function TopologyDiagram() {
  const { byRole } = useBooks()
  const research = byRole('research')
  const validation = byRole('validation')
  const live = byRole('live')

  const books: Record<'research' | 'validation' | 'live', Book | undefined> = {
    research,
    validation,
    live,
  }

  const renderNode = (role: 'research' | 'validation' | 'live') => {
    const book = books[role]
    const pos = NODE_POS[role]
    const label = ROLE_LABEL[role]
    const dashed = !book
    return (
      <g key={role} transform={`translate(${pos.x}, ${pos.y})`}>
        <rect
          className={nodeClass(book)}
          x={0}
          y={0}
          width={NODE_W}
          height={NODE_H}
          rx={10}
          strokeDasharray={dashed ? '4 3' : undefined}
        />
        <text className="topo-node-label" x={NODE_W / 2} y={22} textAnchor="middle">
          {label}
        </text>
        <text className="topo-node-sub" x={NODE_W / 2} y={40} textAnchor="middle">
          {!book
            ? 'not created'
            : book.bookStatus === null
              ? 'dormant'
              : book.bookStatus.halted
                ? 'halted'
                : 'running'}
        </text>
      </g>
    )
  }

  // Feed edges: a book with pick_source_account_id pointing at research is
  // fed from it. Live's edge is drawn muted/dashed since it's dormant.
  const validationFed = validation?.pick_source_account_id === research?.id
  const liveFed = live?.pick_source_account_id === research?.id

  const researchRight = { x: NODE_POS.research.x + NODE_W, y: NODE_POS.research.y + NODE_H / 2 }
  const validationLeft = { x: NODE_POS.validation.x, y: NODE_POS.validation.y + NODE_H / 2 }
  const liveLeft = { x: NODE_POS.live.x, y: NODE_POS.live.y + NODE_H / 2 }

  return (
    <div className="card topology-diagram">
      <div className="card-header">
        <h3 className="card-title">Book topology</h3>
      </div>
      <div className="topology-svg-wrap">
        <svg
          viewBox="0 0 480 240"
          preserveAspectRatio="xMidYMid meet"
          className="topology-svg"
          role="img"
          aria-label="Three-book feed topology: research feeds validation and live"
        >
          <defs>
            <marker id="topo-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" className="topo-arrowhead" />
            </marker>
            <marker id="topo-arrow-muted" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" className="topo-arrowhead-muted" />
            </marker>
          </defs>

          {/* research -> validation */}
          <path
            d={`M ${researchRight.x} ${researchRight.y} C ${researchRight.x + 90} ${researchRight.y}, ${validationLeft.x - 90} ${validationLeft.y}, ${validationLeft.x - 6} ${validationLeft.y}`}
            className={validationFed ? 'topo-edge' : 'topo-edge topo-edge--muted'}
            markerEnd={validationFed ? 'url(#topo-arrow)' : 'url(#topo-arrow-muted)'}
          />
          {validationFed && (
            <text x={(researchRight.x + validationLeft.x) / 2} y={researchRight.y - 42} textAnchor="middle" className="topo-edge-label">
              picks feed (HELD set)
            </text>
          )}

          {/* research -> live (dashed/muted — dormant) */}
          <path
            d={`M ${researchRight.x} ${researchRight.y} C ${researchRight.x + 90} ${researchRight.y}, ${liveLeft.x - 90} ${liveLeft.y}, ${liveLeft.x - 6} ${liveLeft.y}`}
            className="topo-edge topo-edge--muted"
            strokeDasharray="5 4"
            markerEnd="url(#topo-arrow-muted)"
          />
          {liveFed && (
            <text x={(researchRight.x + liveLeft.x) / 2} y={liveLeft.y + 34} textAnchor="middle" className="topo-edge-label topo-edge-label--muted">
              picks feed (HELD set) · dormant
            </text>
          )}

          {renderNode('research')}
          {renderNode('validation')}
          {renderNode('live')}
        </svg>
      </div>
    </div>
  )
}
